import { Entity, EntityDimensions, Location, Movement, Weapon, WeaponCharacteristics } from "./models"
import { degrees_to_radians, radians_to_degrees, randomId } from "./util"

export const emitWeaponParticles = (weapon: Weapon, originLocation: Location, originMovement: Movement): Entity[] => {
    const characteristics = WeaponCharacteristics[weapon?.weaponType || "none"]
    if (!characteristics) {
        return []
    }

    const originLocationToUse = {
        ...originLocation,
    }
    if (characteristics.muzzleFireOffset) {
        const speed2X = characteristics.muzzleFireOffset * Math.cos(originMovement.angle)
        const speed2Y = characteristics.muzzleFireOffset * Math.sin(originMovement.angle)
        originLocationToUse.x += speed2X
        originLocationToUse.y += speed2Y
    }

    if (characteristics.maxParticles === 1) {
        const bullet = createNewBullet(weapon, originLocationToUse, originMovement)
        bullet.spriteId = characteristics.bulletSpriteId
        bullet.flags.originWeaponType = weapon?.weaponType || "none"
        return bullet ? [bullet] : []
    }

    const bullets = []
    const originAngle = originMovement.angle
    const bulletBatch = randomId()

    const particles = characteristics.maxParticles
    for (let i = -(particles / 2); i < particles - particles / 2; i++) {
        let degrees = radians_to_degrees(originAngle) + 15 + i * 30
        const bullet = createNewBullet(weapon, originLocationToUse, {
            ...originMovement,
            angle: degrees_to_radians(degrees),
        })
        if (bullet) {
            bullet.flags.originWeaponType = weapon?.weaponType || "none"
            bullet.spriteId = characteristics.bulletSpriteId
            bullet.context = { bullet: { batchId: bulletBatch } }
            bullets.push(bullet)
        }
    }
    return bullets
}

/**
 * return an un-id'd bullet
 */
export const createNewBullet = (
    weapon: Weapon,
    originLocation: Location,
    originMovement: Movement,
): Entity | undefined => {
    if (!originLocation || !originMovement) {
        return
    }
    const characteristics = WeaponCharacteristics[weapon?.weaponType || "none"]
    if (!characteristics) {
        return
    }
    // Cardinal headings must have a truly stationary perpendicular axis. Floating-point
    // cos(PI / 2) otherwise triggers a sideways wall probe while the arrow moves south.
    const cos = Math.cos(originMovement.angle), sin = Math.sin(originMovement.angle)
    const dx = Math.abs(cos) < 1e-10 ? 0 : cos
    const dy = Math.abs(sin) < 1e-10 ? 0 : sin
    const bullet = new Entity()
    bullet.maxDistance = characteristics.maxDistance
    bullet.entityType = "bullet"
    bullet.origin = {
        x: originLocation.x + dx,
        y: originLocation.y + dy,
    }
    bullet.location = { x: bullet.origin?.x, y: bullet.origin?.y }
    bullet.movement = { ...originMovement }
    bullet.dimensions = characteristics.collisionDimensions || { ...EntityDimensions[bullet.entityType] }

    bullet.speed = characteristics.bulletSpeed
    bullet.speed2.x = dx * characteristics.bulletSpeed
    bullet.speed2.y = dy * characteristics.bulletSpeed
    bullet.roomId = originLocation.roomId
    bullet.flags = {}

    return bullet
}
